// web/src/pages/Copyright.tsx
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE_TEXT = "August 21, 2025";
const LAST_UPDATED_TEXT = "August 21, 2025";
const SUPPORT_EMAIL = "support@spellgather.com";
const COPYRIGHT_AGENT_EMAIL = "support@spellgather.com"; // same inbox; routed to copyright agent

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-8 mb-3">
        {title}
      </h2>
      <div className="prose prose-invert max-w-none">{children}</div>
    </section>
  );
}

export default function Copyright(): JSX.Element {
  return (
    <main className="mx-auto max-w-4xl p-6">
      <Card className="bg-gray-900 border-gray-700 shadow-xl">
        <CardHeader>
          <CardTitle className="text-white">
            Copyright &amp; Fan Content Notice
          </CardTitle>
          <CardDescription className="text-white/90">
            Effective: {EFFECTIVE_DATE_TEXT} · Last Updated: {LAST_UPDATED_TEXT}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-white">
          {/* TOC */}
          <nav aria-label="Table of contents" className="mb-6">
            <ul className="text-sm grid sm:grid-cols-2 gap-2">
              <li>
                <a className="underline" href="#scope">
                  1. Scope & Relationship to Other Policies
                </a>
              </li>
              <li>
                <a className="underline" href="#ownership">
                  2. Ownership & Trademarks
                </a>
              </li>
              <li>
                <a className="underline" href="#fan-content">
                  3. Fan Content Notice (Wizards of the Coast)
                </a>
              </li>
              <li>
                <a className="underline" href="#your-content">
                  4. Your Content & License to Spellgather
                </a>
              </li>
              <li>
                <a className="underline" href="#prohibited">
                  5. Prohibited Content & Fair Use
                </a>
              </li>
              <li>
                <a className="underline" href="#dmca">
                  6. DMCA Notice & Takedown Process
                </a>
              </li>
              <li>
                <a className="underline" href="#counter">
                  7. Counter-Notice
                </a>
              </li>
              <li>
                <a className="underline" href="#repeat">
                  8. Repeat Infringer Policy
                </a>
              </li>
              <li>
                <a className="underline" href="#reporting">
                  9. Reporting IP Infringement
                </a>
              </li>
              <li>
                <a className="underline" href="#changes">
                  10. Changes to this Notice
                </a>
              </li>
              <li>
                <a className="underline" href="#contact">
                  Contact / Copyright Agent
                </a>
              </li>
            </ul>
          </nav>

          <Section id="scope" title="1) Scope & Relationship to Other Policies">
            <p>
              This page explains how copyright, trademarks, and fan content are
              handled on Spellgather. It complements our{" "}
              <Link className="underline" to="/terms">
                Terms of Service
              </Link>
              ,{" "}
              <Link className="underline" to="/privacy">
                Privacy Policy
              </Link>
              ,{" "}
              <Link className="underline" to="/community">
                Community Guidelines
              </Link>
              , and{" "}
              <Link className="underline" to="/data">
                Data Sharing &amp; Protection Statement
              </Link>
              . In the event of a conflict, the Terms of Service control
              contractual rights and obligations.
            </p>
          </Section>

          <Section id="ownership" title="2) Ownership & Trademarks">
            <ul>
              <li>
                <strong>User-Generated Content (UGC):</strong> You retain
                ownership of content you create and upload, subject to the
                license you grant to Spellgather below.
              </li>
              <li>
                <strong>Third-Party Rights:</strong> Many elements referenced by
                the community (e.g., Magic: The Gathering® names, logos, card
                images) are the intellectual property of their respective
                owners.
              </li>
              <li>
                <strong>Non-Affiliation:</strong> Spellgather is an independent
                fan community and marketplace. It is not produced by, endorsed
                by, or affiliated with Wizards of the Coast LLC or Hasbro, Inc.
              </li>
              <li>
                <strong>Attribution:</strong> Magic: The Gathering® and all
                related marks and logos are trademarks of Wizards of the Coast
                LLC in the U.S.A. and other countries. Other product and company
                names are trademarks of their respective holders.
              </li>
              <li>
                <strong>Card Data / Images:</strong> Where card data or imagery
                is displayed, it is provided solely for community, collection,
                discovery, and educational purposes. If required by a
                rightsholder, we will promptly remove or replace specific
                assets.
              </li>
            </ul>
          </Section>

          <Section
            id="fan-content"
            title="3) Fan Content Notice (Wizards of the Coast)"
          >
            <p>
              Spellgather hosts player collections, decklists, and discussions
              that may reference Magic: The Gathering®. We strive to respect
              Wizards of the Coast’s fan content guidelines. In practical terms,
              that means:
            </p>
            <ul>
              <li>
                We do not charge users for access to Wizards’ IP itself, and we
                avoid implying endorsement.
              </li>
              <li>
                We avoid misleading use of Wizards’ trademarks or trade dress
                and include clear non-affiliation notices.
              </li>
              <li>
                We remove or modify content that appears to misuse Wizards’ IP
                if contacted by Wizards or their agents.
              </li>
            </ul>
            <p>
              If you believe any content on Spellgather conflicts with
              applicable fan content policies or your rights, please see{" "}
              <a className="underline" href="#reporting">
                Reporting IP Infringement
              </a>
              .
            </p>
          </Section>

          <Section
            id="your-content"
            title="4) Your Content & License to Spellgather"
          >
            <p className="mb-2">
              By uploading, posting, or otherwise providing content to
              Spellgather (including images, text, listings, offers, messages,
              and profile elements), you represent that you have all necessary
              rights and licenses, and you grant Spellgather a worldwide,
              non-exclusive, royalty-free, sublicensable, and transferable
              license to host, store, reproduce, adapt, publish, publicly
              display, and distribute that content solely to operate, promote,
              and improve the Service. This license ends when your content is
              removed from the Service, except where reasonably necessary for
              backups, audits, dispute resolution, or compliance with law.
              Nothing in this section limits your ownership of your UGC.
            </p>
            <p className="mt-2">
              You agree not to upload content that infringes others’ rights,
              violates the{" "}
              <Link className="underline" to="/community">
                Community Guidelines
              </Link>
              , or is otherwise unlawful. You are responsible for the content
              you submit and for obtaining any required permissions.
            </p>
          </Section>

          <Section id="prohibited" title="5) Prohibited Content & Fair Use">
            <ul>
              <li>
                <strong>Prohibited:</strong> Uploads that misappropriate
                copyrighted works (e.g., full rulebooks, scans of paid content
                without permission), use others’ trademarks in a way that causes
                confusion, or distribute confidential or illegal materials.
              </li>
              <li>
                <strong>Transformative & Limited Use:</strong> Commentary,
                education, and small excerpts may qualify as fair use in some
                jurisdictions. Fair use is fact-specific; when in doubt, obtain
                permission.
              </li>
              <li>
                <strong>Logos & UI Elements:</strong> Do not replicate Wizards’
                trade dress or imply partnership.
              </li>
            </ul>
          </Section>

          <Section id="dmca" title="6) DMCA Notice & Takedown Process">
            <p className="mb-2">
              If you are a copyright owner or authorized agent and believe
              content on Spellgather infringes your rights, you may submit a
              notice under the Digital Millennium Copyright Act (17 U.S.C. §512)
              to our Copyright Agent. Your notice must include <em>all</em> of
              the following:
            </p>
            <ol>
              <li>
                A physical or electronic signature of a person authorized to act
                for the owner.
              </li>
              <li>
                Identification of the copyrighted work claimed to have been
                infringed (or a representative list).
              </li>
              <li>
                Identification of the material claimed to be infringing and
                information reasonably sufficient for us to locate it (URL,
                profile link, message ID, or similar).
              </li>
              <li>
                Your contact information (name, address, phone, and email).
              </li>
              <li>
                A statement that you have a good-faith belief the use is not
                authorized by the copyright owner, its agent, or the law.
              </li>
              <li>
                A statement that the information in the notice is accurate, and
                under penalty of perjury, that you are authorized to act for the
                owner.
              </li>
            </ol>
            <p className="mt-2">
              Send notices to:{" "}
              <a
                className="underline"
                href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Notice`}
              >
                {COPYRIGHT_AGENT_EMAIL}
              </a>
              . We may notify the user who uploaded the content and, when
              appropriate, remove or disable access to the material pending
              review.
            </p>
          </Section>

          <Section id="counter" title="7) Counter-Notice">
            <p className="mb-2">
              If your content was removed under a DMCA notice and you believe
              the removal is a mistake or misidentification, you may submit a
              counter-notice containing:
            </p>
            <ol>
              <li>Your physical or electronic signature.</li>
              <li>
                Identification of the removed material and where it appeared
                before removal.
              </li>
              <li>
                A statement under penalty of perjury that you have a good-faith
                belief the material was removed or disabled due to mistake or
                misidentification.
              </li>
              <li>
                Your name, address, phone number, and a statement that you
                consent to the jurisdiction of the Federal District Court for
                your address (or any judicial district where we are located if
                outside the U.S.), and that you will accept service of process
                from the original complainant or their agent.
              </li>
            </ol>
            <p className="mt-2">
              Send counter-notices to:{" "}
              <a
                className="underline"
                href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Counter-Notice`}
              >
                {COPYRIGHT_AGENT_EMAIL}
              </a>
              . If we receive a valid counter-notice, we may restore the
              material unless the complainant informs us within a reasonable
              time that they have filed a court action seeking to restrain your
              activity.
            </p>
          </Section>

          <Section id="repeat" title="8) Repeat Infringer Policy">
            <p>
              In appropriate circumstances, and at our discretion, Spellgather
              may terminate or restrict accounts of users who are determined to
              be repeat infringers. We may also limit features, remove content,
              and/or apply other measures consistent with our{" "}
              <Link className="underline" to="/terms">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link className="underline" to="/community">
                Community Guidelines
              </Link>
              .
            </p>
          </Section>

          <Section
            id="reporting"
            title="9) Reporting IP Infringement (Non-DMCA)"
          >
            <p>
              For trademark concerns, fan content policy issues, or other IP
              questions that may not require a formal DMCA process, contact us
              at{" "}
              <a
                className="underline"
                href={`mailto:${SUPPORT_EMAIL}?subject=IP%20Concern`}
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              with sufficient detail to evaluate the concern (links,
              screenshots, and a description of the issue). We aim to respond
              promptly.
            </p>
          </Section>

          <Section id="changes" title="10) Changes to this Notice">
            <p>
              We may update this page to reflect legal requirements or platform
              changes. Material updates will be announced in-app and/or by email
              where appropriate. Continued use of the Service after the
              effective date indicates acceptance of the updated terms.
            </p>
          </Section>

          <Section id="contact" title="Contact / Copyright Agent">
            <p>
              Copyright Agent:{" "}
              <a className="underline" href={`mailto:${COPYRIGHT_AGENT_EMAIL}`}>
                {COPYRIGHT_AGENT_EMAIL}
              </a>
            </p>
            <p className="mt-2">
              For general questions, contact{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              . See also{" "}
              <Link className="underline" to="/terms">
                Terms of Service
              </Link>
              ,{" "}
              <Link className="underline" to="/privacy">
                Privacy Policy
              </Link>
              ,{" "}
              <Link className="underline" to="/community">
                Community Guidelines
              </Link>
              , and{" "}
              <Link className="underline" to="/data">
                Data Sharing &amp; Protection
              </Link>
              .
            </p>
          </Section>

          {/* Convenience CTAs */}
          <div className="mt-8 flex flex-wrap gap-2">
            <a href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Notice`}>
              <Button className="bg-purple-600 hover:bg-purple-700">
                Send DMCA Notice
              </Button>
            </a>
            <a
              href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Counter-Notice`}
            >
              <Button
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
              >
                Send Counter-Notice
              </Button>
            </a>
            <Link to="/community">
              <Button
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
              >
                Review Community Guidelines
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

